'use client'

import { useState } from 'react'
import Image from 'next/image'
import { useKeenSlider } from 'keen-slider/react'
import 'keen-slider/keen-slider.min.css'
import { getStrapiMedia } from '@/lib/strapi/client'
import type { TeaserImageGridItemType } from '@/lib/types/teaser'

type Props = {
  items: TeaserImageGridItemType[]
  onImageClick?: (index: number) => void
}

function NavArrow({ direction }: { direction: 'prev' | 'next' }) {
  return (
    <svg width="14" height="16" viewBox="0 0 14 16" fill="none" aria-hidden="true">
      <path
        d={direction === 'prev' ? 'M9 3L4 8L9 13' : 'M5 3L10 8L5 13'}
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

export default function TeaserImageGridCarousel({ items, onImageClick }: Props) {
  const [current, setCurrent] = useState(0)
  const [pageCount, setPageCount] = useState(1)

  const [sliderRef, instanceRef] = useKeenSlider<HTMLDivElement>({
    mode: 'snap',
    rubberband: false,
    defaultAnimation: {
      duration: 450,
      easing: (t) => 1 - Math.pow(1 - t, 3),
    },
    slides: {
      perView: 2.1,
      spacing: 12,
    },
    breakpoints: {
      '(min-width: 62rem)': {
        slides: {
          perView: 3,
          spacing: 20,
        },
      },
    },
    created(slider) {
      setPageCount(slider.track.details.maxIdx + 1)
      setCurrent(slider.track.details.rel)
    },
    slideChanged(slider) {
      setCurrent(slider.track.details.rel)
    },
    updated(slider) {
      setPageCount(slider.track.details.maxIdx + 1)
      setCurrent(slider.track.details.rel)
    },
  })

  if (items.length === 0) return null

  const showControls = pageCount > 1

  return (
    <div className="flex w-full flex-col gap-4 md:gap-8" data-fig="teaser-image-grid-carousel">
      <div
        ref={sliderRef}
        className="keen-slider w-full"
        role="list"
        aria-label="Photo gallery"
      >
        {items.map((item, index) => {
          const alt =
            item.image?.alternativeText?.trim() ||
            item.image?.caption?.trim() ||
            `Gallery image ${index + 1}`
          return (
            <div
              key={item.id}
              role="listitem"
              className="keen-slider__slide relative h-[200px] overflow-hidden rounded-[12px] shadow-[0px_1px_2px_rgba(0,0,0,0.05)] md:aspect-[400/300] md:h-auto md:rounded-[20px]"
            >
              <button
                type="button"
                onClick={() => onImageClick?.(index)}
                className="absolute inset-0 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF8C18] focus-visible:ring-inset"
                aria-label={`Open ${alt} in gallery`}
              >
                {item.image ? (
                  <Image
                    src={getStrapiMedia(item.image.url)}
                    alt={alt}
                    fill
                    className="object-cover"
                    sizes="(max-width: 991px) 50vw, 33vw"
                  />
                ) : (
                  <div className="h-full w-full bg-surface-linen" />
                )}
              </button>
            </div>
          )
        })}
      </div>

      {showControls && (
        <div className="flex w-full items-center justify-end gap-4">
          <div className="flex items-center gap-2" role="group" aria-label="Gallery pages">
            {Array.from({ length: pageCount }, (_, index) => {
              const active = index === current
              return (
                <button
                  key={index}
                  type="button"
                  aria-current={active ? 'true' : undefined}
                  aria-label={`Go to slide group ${index + 1}`}
                  onClick={() => instanceRef.current?.moveToIdx(index)}
                  className={
                    active
                      ? 'h-2 w-8 shrink-0 rounded-full bg-[#FF8C18] transition-all'
                      : 'size-2 shrink-0 rounded-full bg-[rgba(255,140,24,0.2)] transition-all hover:bg-[rgba(255,140,24,0.4)]'
                  }
                />
              )
            })}
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              aria-label="Previous photos"
              onClick={() => instanceRef.current?.prev()}
              className="flex size-10 shrink-0 cursor-pointer items-center justify-center rounded-full border border-border bg-surface-white text-text-dark shadow-[0px_1px_2px_rgba(0,0,0,0.05)] transition-colors hover:bg-surface-linen focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF8C18]"
            >
              <NavArrow direction="prev" />
            </button>
            <button
              type="button"
              aria-label="Next photos"
              onClick={() => instanceRef.current?.next()}
              className="flex size-10 shrink-0 cursor-pointer items-center justify-center rounded-full border border-border bg-surface-white text-text-dark shadow-[0px_1px_2px_rgba(0,0,0,0.05)] transition-colors hover:bg-surface-linen focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF8C18]"
            >
              <NavArrow direction="next" />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
